import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import { ICompany, IMergeResult } from '~/client/graph'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import {
  ACTIVATE_COMPANIES,
  DELETE_COMPANIES,
  MERGE_COMPANIES,
  SAVE_COMPANIES,
} from '~/client/src/shared/stores/EventStore/eventConstants'
import { toast } from '~/client/src/shared/utils/toast'

import MergingSummary from '../MergingSummary'

interface ProvidedStores {
  companiesStore: CompaniesStore
  state: DesktopInitialState
}

export type MutationResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly errorMessage: string }

export interface CompanyMutations {
  readonly isUpdating: boolean
  readonly saveCompanies: (
    companies: ICompany[],
    isAdding: boolean,
  ) => Promise<MutationResult>
  readonly bulkSaveCompanies: (companies: ICompany[]) => Promise<MutationResult>
  readonly mergeCompanies: (
    companies: ICompany[],
    targetCompanyId: string,
  ) => Promise<MutationResult>
  readonly deactivateCompanies: (
    companyIds: string[],
  ) => Promise<MutationResult>
  readonly activateCompanies: (companyIds: string[]) => Promise<MutationResult>
}

const UPDATING_EVENTS = [
  SAVE_COMPANIES,
  DELETE_COMPANIES,
  ACTIVATE_COMPANIES,
  MERGE_COMPANIES,
]

const MERGE_SUMMARY_TOAST_TIMEOUT = 15000

const OK: MutationResult = { ok: true }

function failure(error: unknown): MutationResult {
  return {
    ok: false,
    errorMessage: error instanceof Error ? error.message : String(error ?? ''),
  }
}

function renderMergeSummary(mergeResult: IMergeResult): React.ReactNode {
  const shouldShowSummary = Object.values(mergeResult).some(
    count => !!count && Number.isInteger(count),
  )

  return shouldShowSummary ? <MergingSummary mergeResult={mergeResult} /> : null
}

export function useCompanyMutations(): CompanyMutations {
  const { companiesStore, state } = React.useContext(
    MobXProviderContext,
  ) as unknown as ProvidedStores

  const isUpdating = UPDATING_EVENTS.some(event => state.loading.get(event))

  const actions = React.useMemo(
    () => ({
      saveCompanies: async (
        companies: ICompany[],
        isAdding: boolean,
      ): Promise<MutationResult> => {
        try {
          await companiesStore.saveMany(companies)
        } catch (error) {
          return failure(error)
        }

        const { companyAdded, companiesUpdated } = Localization.translator
        toast.success(isAdding ? companyAdded : companiesUpdated)
        return OK
      },
      bulkSaveCompanies: async (
        companies: ICompany[],
      ): Promise<MutationResult> => {
        try {
          await companiesStore.saveMany(companies)
        } catch (error) {
          return failure(error)
        }
        return OK
      },
      mergeCompanies: async (
        companies: ICompany[],
        targetCompanyId: string,
      ): Promise<MutationResult> => {
        const targetCompany = companies.find(c => c.id === targetCompanyId)

        let mergeResult: IMergeResult
        try {
          mergeResult = await companiesStore.mergeMany(
            companies.map(c => c.id),
            targetCompany,
          )
        } catch (error) {
          return failure(error)
        }

        const count = companies.length - 1
        const title = `${Localization.translator.xCompanies(count)} ${
          count > 1 ? 'have' : 'has'
        } been merged into [${targetCompany.name}]`

        toast.success(title, {
          timeout: MERGE_SUMMARY_TOAST_TIMEOUT,
          description: renderMergeSummary(mergeResult),
        })
        return OK
      },
      deactivateCompanies: async (
        companyIds: string[],
      ): Promise<MutationResult> => {
        try {
          await companiesStore.deleteMany(companyIds)
        } catch (error) {
          return failure(error)
        }
        return OK
      },
      activateCompanies: async (
        companyIds: string[],
      ): Promise<MutationResult> => {
        try {
          await companiesStore.activateMany(companyIds)
        } catch (error) {
          return failure(error)
        }
        return OK
      },
    }),
    [companiesStore],
  )

  return { isUpdating, ...actions }
}
